import { useCallback, useMemo, useState } from 'react';

import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { Product } from '@hotjar/webapp/domains/common/product/types';
import {
  getV3SkuFromPlanId,
  isLegacyPlanId,
  type LegacyPlanId,
  type Sku,
} from '@hotjar/product-catalog';

export enum MultiProductStep {
  ProductSelection = 'ProductSelection',
  PlanSelection = 'PlanSelection',
}

export type SelectedProductTypes = Record<ProductType, boolean>;

export type SelectedProducts = Partial<Record<ProductType, Product>>;

export type SetSelectedProductTypes = (newSelectedProductTypes: SelectedProductTypes) => void;

export type ChangeSelectedProductTypes = (productType: ProductType) => void;

export type ChangeSelectedProducts = (productType: ProductType, product: Product) => void;

interface MultiProductsState {
  step: MultiProductStep;
  selectedProductTypes: SelectedProductTypes;
  selectedProducts: SelectedProducts;
  selectedSkus: Array<Sku>;
}

const MULTI_PRODUCTS_MODAL_STEPS: MultiProductStep[] = [
  MultiProductStep.ProductSelection,
  MultiProductStep.PlanSelection,
];

export interface UseMultiProductsState {
  step: MultiProductStep;
  selectedProductTypes: SelectedProductTypes;
  setSelectedProductTypes: SetSelectedProductTypes;
  changeSelectedProductTypes: ChangeSelectedProductTypes;
  selectedProducts: SelectedProducts;
  changeSelectedProducts: ChangeSelectedProducts;
  selectedSkus: Array<Sku>;
  goNextStep: () => void;
  goPrevStep: () => void;
}

export const MULTI_PRODUCTS_INITIAL_STATE: MultiProductsState = {
  step: MultiProductStep.ProductSelection,
  selectedProductTypes: {
    [ProductType.Analytics]: true,
    [ProductType.Feedback]: true,
  },
  selectedProducts: {},
  selectedSkus: [],
};

/**
 * This hook, is meant to handle the top-level modal stepping.
 * At the moment, there are two modals in the MultiProducts Flow.
 * The first one, is about selecting the Product a User plans to purchase(Ask, Observe, etc) and
 * the second one, is about selecting the correct "Plan" for the previously selected Product.
 * The PlanSelection modal, keeps a separate stepping mechanism to navigate between the different Products.
 */
export const useMultiProductsState = (): UseMultiProductsState => {
  const [{ step, selectedProductTypes, selectedProducts }, setMultiProductState] =
    useState<MultiProductsState>(MULTI_PRODUCTS_INITIAL_STATE);

  // Replaces the `selectedProductTypes` object in the state with a new one
  const setSelectedProductTypes = (newSelectedProductTypes: SelectedProductTypes) => {
    setMultiProductState((prevState) => {
      return {
        ...prevState,
        selectedProductTypes: newSelectedProductTypes,
      };
    });
  };

  /**
   * Toggles a given product type's boolean value in the `selectedProductTypes` object
   *
   * TODO: rename this to `toggleSelectedProductTypes`
   */
  const changeSelectedProductTypes = (productId: ProductType) => {
    setMultiProductState((prevState) => {
      const { selectedProductTypes: prevSelectedProductTypes } = prevState;
      return {
        ...prevState,
        selectedProductTypes: {
          ...prevSelectedProductTypes,
          [productId]: !prevSelectedProductTypes[productId],
        },
      };
    });
  };

  const changeSelectedProducts = (productType: ProductType, product: Product) => {
    setMultiProductState((prevState) => {
      const { selectedProducts: prevSelectedProducts } = prevState;
      return {
        ...prevState,
        selectedProducts: {
          ...prevSelectedProducts,
          [productType]: product,
        },
      };
    });
  };

  const selectedSkus = useMemo(() => {
    return Object.values(selectedProducts)
      .map((product) => product.sku)
      .filter((legacyPlanId): legacyPlanId is LegacyPlanId => isLegacyPlanId(legacyPlanId))
      .map((legacyPlanId) => getV3SkuFromPlanId(legacyPlanId));
  }, [selectedProducts]);

  const goNextStep = useCallback(() => {
    const currentStepIndex = MULTI_PRODUCTS_MODAL_STEPS.findIndex((stepName) => stepName === step);

    let nextStep = MULTI_PRODUCTS_MODAL_STEPS[currentStepIndex + 1];

    if (currentStepIndex === MULTI_PRODUCTS_MODAL_STEPS.length - 1) {
      // can't go to a step higher than the max number of steps
      nextStep = MULTI_PRODUCTS_MODAL_STEPS[MULTI_PRODUCTS_MODAL_STEPS.length - 1];
    }

    setMultiProductState((prevState) => {
      return {
        ...prevState,
        step: nextStep,
      };
    });
  }, [step]);

  const goPrevStep = useCallback(() => {
    const currentStepIndex = MULTI_PRODUCTS_MODAL_STEPS.findIndex((stepName) => stepName === step);

    let prevStep: MultiProductStep;

    if (currentStepIndex > 0) {
      prevStep = MULTI_PRODUCTS_MODAL_STEPS[currentStepIndex - 1];
    } else {
      prevStep = MULTI_PRODUCTS_MODAL_STEPS[currentStepIndex];
    }

    setMultiProductState((prevState) => {
      return {
        ...prevState,
        step: prevStep,
      };
    });
  }, [step]);

  return {
    step,
    selectedProductTypes,
    changeSelectedProductTypes,
    setSelectedProductTypes,
    selectedProducts,
    changeSelectedProducts,
    selectedSkus,
    goNextStep,
    goPrevStep,
  };
};
